// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, Grid, InputLabel, MenuItem, Paper, Select, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import api from '../../utils/api';
import { NOT_PROVIDED } from '../../utils/formatFacturation';
import { today, shortDate, dayToIso } from '../../utils/dates';
import { errorMessage } from '../../utils/erreurs';

const REGIMES = {
  accepte: { label: 'Régime b) accepté', couleur: 'success' },
  refus_partiel: { label: 'Refus partiel', couleur: 'warning' },
  refuse: { label: "Refusé pour tout l'établissement", couleur: 'error' },
};
const SCOPES = { site: "Tout l'établissement", room: 'Une chambre', resident: 'Un résident' };

const EMPTY_REJECTION = {
  scope: 'site', property_id: '', resident_id: '', notified_at: '', effective_at: '',
  reason: '', source: '',
};

function rate(v) {
  return v == null ? 'Non mesuré' : `${Number(v).toFixed(2)} / sem.-chambre`;
}

/**
 * Engagements du contrat d'un établissement (`pro_engagements.py`) : régime
 * d'amélioration et ses refus (art. 9.3 d), palier de fausses alertes,
 * manquements et droits à avoir (art. 7.2 b à d). Un droit à avoir se traite à
 * la main dans Facturation, onglet Avoirs. Le back-office ne liste pas les
 * périodes d'observation : l'identifiant se reprend de la console de
 * l'établissement, et la période se mesure avant que le palier ne soit figé.
 */
export default function CommitmentsBlock({ site, chambres: rooms = [], onToast }) {
  const [state, setState] = useState({ chargement: true, annexe: null, trajectoire: null, erreur: '' });
  const [dialogue, setDialogue] = useState(null);

  const charger = useCallback(async () => {
    setState((e) => ({ ...e, chargement: true, erreur: '' }));
    try {
      const [annex, trajectory] = await Promise.all([
        api.get(`/pro/admin/sites/${site.id}/annex2`),
        api.get(`/pro/admin/sites/${site.id}/false-alert-baseline`),
      ]);
      setState({ chargement: false, annexe: annex.data, trajectoire: trajectory.data, erreur: '' });
    } catch (e) {
      setState({ chargement: false, annexe: null, trajectoire: null, erreur: errorMessage(e, 'Engagements illisibles') });
    }
  }, [site.id]);

  useEffect(() => { charger(); }, [charger]);

  const update = (patch) => setDialogue((d) => (d ? { ...d, valeurs: { ...d.valeurs, ...patch } } : d));
  const failure = (d, e) => setDialogue({ ...d, enCours: false, erreur: errorMessage(e, 'Refusé par le serveur') });

  const recordRejection = async () => {
    const d = dialogue;
    setDialogue({ ...d, enCours: true, erreur: '' });
    const v = d.valeurs;
    try {
      await api.post(`/pro/admin/sites/${site.id}/improvement-refusals`, {
        scope: v.scope,
        property_id: v.scope === 'room' ? (v.property_id || null) : null,
        resident_id: v.scope === 'resident' ? (v.resident_id.trim() || null) : null,
        notified_at: dayToIso(v.notified_at),
        effective_at: dayToIso(v.effective_at),
        reason: v.reason.trim() || null,
        source: v.source.trim() || null,
      });
      setDialogue(null);
      onToast?.('Refus inscrit à l\'annexe 2');
      charger();
    } catch (e) { failure(d, e); }
  };

  const liftRejection = async () => {
    const d = dialogue;
    setDialogue({ ...d, enCours: true, erreur: '' });
    try {
      await api.post(`/pro/admin/sites/${site.id}/improvement-refusals/${d.refus.id}/withdraw`, {
        withdrawn_at: dayToIso(d.valeurs.withdrawn_at),
        source: d.valeurs.source.trim() || null,
      });
      setDialogue(null);
      onToast?.('Refus levé pour l\'avenir');
      charger();
    } catch (e) { failure(d, e); }
  };

  const measure = async () => {
    const d = dialogue;
    const timeWindow = d.valeurs.window_id.trim();
    if (!timeWindow) return;
    setDialogue({ ...d, enCours: true, erreur: '', mesure: null });
    try {
      const r = await api.get(`/pro/admin/sites/${site.id}/false-alert-baseline/measure`,
        { params: { window_id: timeWindow } });
      setDialogue({ ...d, enCours: false, erreur: '', mesure: r.data, mesureDe: timeWindow });
    } catch (e) { failure({ ...d, mesure: null }, e); }
  };

  const recordBaseline = async () => {
    const d = dialogue;
    setDialogue({ ...d, enCours: true, erreur: '' });
    try {
      await api.post(`/pro/admin/sites/${site.id}/false-alert-baseline`, {
        window_id: d.mesureDe,
        counterpart: d.valeurs.counterpart.trim() || null,
      });
      setDialogue(null);
      onToast?.('Palier constaté');
      charger();
    } catch (e) { failure(d, e); }
  };

  const savePlan = async () => {
    const d = dialogue;
    setDialogue({ ...d, enCours: true, erreur: '' });
    try {
      await api.post(`/pro/admin/false-alert-breaches/${d.manquement.id}/correction-plan`, {
        reference: d.valeurs.reference.trim() || null,
        delivered_at: dayToIso(d.valeurs.delivered_at),
      });
      setDialogue(null);
      onToast?.('Remise du plan de correction inscrite');
      charger();
    } catch (e) { failure(d, e); }
  };

  const regime = state.annexe?.improvement_regime;
  const baseline = state.trajectoire?.baseline;
  const month = state.trajectoire?.months || [];
  const breaches = state.trajectoire?.breaches || [];
  const rejection = regime?.refusals || [];
  const etiquetteRegime = REGIMES[regime?.state] || { label: regime?.state || 'inconnu', couleur: 'default' };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2">
          {site.name}
          {site.city ? <Typography component="span" variant="caption" color="text.secondary"> · {site.city}</Typography> : null}
        </Typography>
        {regime && <Chip size="small" label={etiquetteRegime.label} color={etiquetteRegime.couleur} />}
      </Box>

      {state.chargement && !state.annexe ? (
        <CircularProgress size={18} />
      ) : state.erreur ? (
        <Alert severity="error">{state.erreur}</Alert>
      ) : (
        <>
          <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
            Régime d&apos;amélioration (art. 9.3 d)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {regime?.refusals_in_force ?? 0} refus en vigueur, {regime?.refusals_pending ?? 0} à venir,
            {' '}{regime?.residents_opposed ?? 0} résident(s) opposé(s) au titre de l&apos;article 9.4
            (valent refus sans autre saisie).
          </Typography>
          {rejection.length > 0 && (
            <Table size="small" sx={{ mb: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Portée</TableCell>
                  <TableCell>Notifié</TableCell>
                  <TableCell>Effet</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>État</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rejection.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      {SCOPES[r.scope] || r.scope}
                      {r.room_label ? ` (${r.room_label})` : ''}
                    </TableCell>
                    <TableCell>{shortDate(r.notified_at)}</TableCell>
                    <TableCell>{shortDate(r.effective_at)}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{r.source || r.reason || NOT_PROVIDED}</TableCell>
                    <TableCell>
                      {r.withdrawn_at ? `Levé le ${shortDate(r.withdrawn_at)}`
                        : r.pending ? 'À venir' : 'En vigueur'}
                    </TableCell>
                    <TableCell align="right">
                      {!r.withdrawn_at && (
                        <Button size="small" onClick={() => setDialogue({
                          type: 'lever', refus: r, erreur: '', enCours: false,
                          valeurs: { withdrawn_at: '', source: '' },
                        })}>
                          Lever
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Button size="small" variant="outlined" sx={{ mb: 2 }}
            onClick={() => setDialogue({
              type: 'refus', erreur: '', enCours: false,
              valeurs: { ...EMPTY_REJECTION, notified_at: today() },
            })}>
            Inscrire un refus
          </Button>

          <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
            Palier de fausses alertes (art. 7.2 b à d)
          </Typography>
          <Alert severity="warning" sx={{ mb: 1 }}>
            Ce palier n&apos;engage que si le contrat SIGNÉ porte l&apos;article 7.2 réécrit (palier
            de référence constaté). Le gabarit cadre_b2b v3 en production porte encore
            l&apos;ancien plafond fixe : un manquement ou un droit signalé ici n&apos;est dû qu&apos;à
            un établissement qui a signé la nouvelle rédaction.
          </Alert>
          {!baseline ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {state.trajectoire?.note || 'Aucun palier constaté.'}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Palier : <strong>{rate(baseline.rate_per_room_week)}</strong>, constaté le
              {' '}{shortDate(baseline.fixed_at)} sur la période du {shortDate(baseline.period?.from)} au
              {' '}{shortDate(baseline.period?.to)} ({baseline.rooms_measured} chambre(s),
              {' '}{baseline.false_positive} faux positif(s), {baseline.unqualified} non qualifié(s))
              {baseline.counterpart ? `, contradictoire avec ${baseline.counterpart}` : ''}.
            </Typography>
          )}
          {month.length > 0 && (
            <Table size="small" sx={{ mb: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Mois</TableCell>
                  <TableCell align="right">Faux positifs</TableCell>
                  <TableCell align="right">Non qualifiés</TableCell>
                  <TableCell align="right">Taux</TableCell>
                  <TableCell>Contre le palier</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {month.map((m) => (
                  <TableRow key={m.month}>
                    <TableCell>{m.month}{m.complete ? '' : ' (en cours)'}</TableCell>
                    <TableCell align="right">{m.false_positive ?? 0}</TableCell>
                    <TableCell align="right">{m.unqualified ?? 0}</TableCell>
                    <TableCell align="right">{rate(m.rate_per_room_week)}</TableCell>
                    <TableCell>
                      {m.over_baseline
                        ? <Chip size="small" color="error" label="Au-dessus" />
                        : <Chip size="small" label="Sous le palier" />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {breaches.map((b) => (
            <Alert key={b.id} sx={{ mb: 1 }}
              severity={b.entitlement && !b.entitlement.handled_at ? 'error' : (b.resolved_at ? 'info' : 'warning')}
              action={!b.plan_delivered_at && !b.resolved_at ? (
                <Button size="small" color="inherit" onClick={() => setDialogue({
                  type: 'plan', manquement: b, erreur: '', enCours: false,
                  valeurs: { reference: '', delivered_at: '' },
                })}>
                  Plan remis
                </Button>
              ) : null}>
              Manquement {(b.months || []).join(' et ')} (palier {rate(b.baseline_rate)}).
              {' '}Plan de correction dû le {shortDate(b.plan_due_at)}
              {b.plan_delivered_at ? `, remis le ${shortDate(b.plan_delivered_at)}${b.plan_reference ? ` (réf. ${b.plan_reference})` : ''}` : ', non remis'}.
              {b.entitlement && (
                <>
                  {' '}<strong>Droit à avoir signalé le {shortDate(b.entitlement.at)}</strong> :
                  {' '}{b.entitlement.label || b.entitlement.reason}.
                  {b.entitlement.handled_at
                    ? ` Traité le ${shortDate(b.entitlement.handled_at)}.`
                    : ' Aucun avoir émis automatiquement : à émettre à la main, puis à déclarer traité dans Facturation, onglet Avoirs.'}
                </>
              )}
            </Alert>
          ))}

          <Button size="small" variant="outlined"
            onClick={() => setDialogue({
              type: 'palier', erreur: '', enCours: false, mesure: null, mesureDe: null,
              valeurs: { window_id: '', counterpart: '' },
            })}>
            {baseline ? 'Constater un nouveau palier' : 'Constater le palier'}
          </Button>
        </>
      )}

      <Dialog open={!!dialogue} onClose={() => !dialogue?.enCours && setDialogue(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogue?.type === 'refus' && `Refus du régime d'amélioration : ${site.name}`}
          {dialogue?.type === 'lever' && `Lever un refus : ${site.name}`}
          {dialogue?.type === 'palier' && `Palier de fausses alertes : ${site.name}`}
          {dialogue?.type === 'plan' && `Remise du plan de correction : ${site.name}`}
        </DialogTitle>
        <DialogContent dividers>
          {dialogue?.erreur && <Alert severity="error" sx={{ mb: 2 }}>{dialogue.erreur}</Alert>}

          {dialogue?.type === 'refus' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="info">
                  Le refus vaut pour l&apos;avenir, et prend effet trente jours au plus après sa
                  notification. Effet vide = dès la notification.
                </Alert>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Portée</InputLabel>
                  <Select label="Portée" value={dialogue.valeurs.scope}
                    onChange={(e) => update({ scope: e.target.value })}>
                    {Object.entries(SCOPES).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                {dialogue.valeurs.scope === 'room' && (
                  <FormControl fullWidth size="small">
                    <InputLabel>Chambre</InputLabel>
                    <Select label="Chambre" value={dialogue.valeurs.property_id}
                      onChange={(e) => update({ property_id: e.target.value })}>
                      {rooms.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.room_number ? `Ch. ${c.room_number}` : c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {dialogue.valeurs.scope === 'resident' && (
                  <TextField fullWidth size="small" label="Identifiant du résident"
                    value={dialogue.valeurs.resident_id}
                    onChange={(e) => update({ resident_id: e.target.value })}
                    helperText="Le back-office ne liste pas les résidents : identifiant repris de la console" />
                )}
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="date" label="Notifié le"
                  InputLabelProps={{ shrink: true }} value={dialogue.valeurs.notified_at}
                  onChange={(e) => update({ notified_at: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="date" label="Prise d'effet"
                  InputLabelProps={{ shrink: true }} value={dialogue.valeurs.effective_at}
                  onChange={(e) => update({ effective_at: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Source (courrier, courriel, PV)"
                  value={dialogue.valeurs.source} onChange={(e) => update({ source: e.target.value })} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Motif" multiline rows={2}
                  value={dialogue.valeurs.reason} onChange={(e) => update({ reason: e.target.value })} />
              </Grid>
            </Grid>
          )}

          {dialogue?.type === 'lever' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2">
                  Refus « {SCOPES[dialogue.refus.scope] || dialogue.refus.scope}
                  {dialogue.refus.room_label ? ` (${dialogue.refus.room_label})` : ''} » notifié le
                  {' '}{shortDate(dialogue.refus.notified_at)}. La ligne reste, datée de sa levée.
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="date" label="Levé le" helperText="Vide : maintenant"
                  InputLabelProps={{ shrink: true }} value={dialogue.valeurs.withdrawn_at}
                  onChange={(e) => update({ withdrawn_at: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Source"
                  value={dialogue.valeurs.source} onChange={(e) => update({ source: e.target.value })} />
              </Grid>
            </Grid>
          )}

          {dialogue?.type === 'palier' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="info">
                  Le palier se constate à l&apos;issue d&apos;une période d&apos;observation du site.
                  Mesurez d&apos;abord : le chiffre affiché est celui qui sera figé.
                </Alert>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField fullWidth size="small" label="Identifiant de la période d'observation"
                  value={dialogue.valeurs.window_id}
                  onChange={(e) => setDialogue((d) => ({ ...d, mesure: null, mesureDe: null, valeurs: { ...d.valeurs, window_id: e.target.value } }))} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button fullWidth variant="outlined" onClick={measure}
                  disabled={dialogue.enCours || !dialogue.valeurs.window_id.trim()}>
                  Mesurer
                </Button>
              </Grid>
              {dialogue.mesure && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="body2">
                      Du {shortDate(dialogue.mesure.period?.from)} au {shortDate(dialogue.mesure.period?.to)}
                      {' '}({dialogue.mesure.period?.days} jours{dialogue.mesure.period?.ended ? '' : ', en cours'}) :
                      {' '}<strong>{rate(dialogue.mesure.rate_per_room_week)}</strong>,
                      {' '}{dialogue.mesure.totals?.false_positive ?? 0} faux positif(s),
                      {' '}{dialogue.mesure.totals?.unqualified ?? 0} non qualifié(s),
                      {' '}{dialogue.mesure.rooms_measured} chambre(s) exposée(s).
                    </Typography>
                    {!dialogue.mesure.meets_contract_duration && (
                      <Typography variant="caption" color="warning.main" display="block">
                        Période plus courte que les {dialogue.mesure.contract_observation_days} jours du contrat.
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              )}
              {dialogue.mesure && (
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Constaté contradictoirement avec"
                    value={dialogue.valeurs.counterpart}
                    onChange={(e) => update({ counterpart: e.target.value })}
                    helperText="Nom et fonction du représentant de l'établissement" />
                </Grid>
              )}
            </Grid>
          )}

          {dialogue?.type === 'plan' && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="date" label="Remis le" helperText="Vide : maintenant"
                  InputLabelProps={{ shrink: true }} value={dialogue.valeurs.delivered_at}
                  onChange={(e) => update({ delivered_at: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Référence du plan"
                  value={dialogue.valeurs.reference} onChange={(e) => update({ reference: e.target.value })} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogue(null)} disabled={dialogue?.enCours}>Annuler</Button>
          {dialogue?.type === 'refus' && (
            <Button variant="contained" onClick={recordRejection} disabled={dialogue.enCours}>
              {dialogue.enCours ? <CircularProgress size={18} /> : 'Inscrire'}
            </Button>
          )}
          {dialogue?.type === 'lever' && (
            <Button variant="contained" onClick={liftRejection} disabled={dialogue.enCours}>
              {dialogue.enCours ? <CircularProgress size={18} /> : 'Lever le refus'}
            </Button>
          )}
          {dialogue?.type === 'palier' && (
            <Button variant="contained" onClick={recordBaseline}
              disabled={dialogue.enCours || !dialogue.mesure || dialogue.mesureDe !== dialogue.valeurs.window_id.trim()}>
              {dialogue.enCours ? <CircularProgress size={18} /> : 'Constater ce palier'}
            </Button>
          )}
          {dialogue?.type === 'plan' && (
            <Button variant="contained" onClick={savePlan} disabled={dialogue.enCours}>
              {dialogue.enCours ? <CircularProgress size={18} /> : 'Inscrire la remise'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
